import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    OnEnterDirective,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { catchError, finalize, from, mergeMap, of, takeUntil, tap } from 'rxjs';
import { UserHttpService } from 'src/providers/user-http.service';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-account-backup-email',
    templateUrl: './account-backup-email.component.html',
    styleUrls: ['./account-backup-email.component.less'],
    imports: [NgIf, WaitComponent, FormsModule, ReactiveFormsModule, MessageBoxComponent, InputComponent,
        AutoFocusDirective, OnEnterDirective, WaitButtonComponent]
})
export class AccountBackupEmailComponent extends BaseComponent implements OnInit {
    //eslint-disable-next-line
    @Output('complete') backupEmailFormSubmitter = new EventEmitter();
    //eslint-disable-next-line
    @Output('close') componentCloser = new EventEmitter();
    @Input() userId;
    @Input() workflow = false;
    @Input() authenticated = false;
    @Input() primaryEmail: string;
    @Input() currentBackupEmail: string;
    backupEmailForm: FormGroup;
    errors: MessageContent;
    loading = true;
    submitting = false;
    submitButtonClass = 'btn btn-verty';
    /**
     * Validates email using WHATWG HTML specification and enhanced to
     * RFC 2821, RFC 2822, RFC 3696 which all depend on RFC 822 for definitions
     * Includes a maxLength of 254
     *
     * Second validator added as a double check. Validators.email allows for abc@xyz
     * This pattern allows all characters permitted by RFC 5322. Some of these could be a security risk
     * if not processed properly such as single quote and pipe. Angular escapes these for us.
     * It also forbids leading, trailing and consecutive dots
     */
    emailFieldValidators = [
        Validators.required,
        Validators.email,
        Validators
            .pattern(/^[A-Z0-9_!#$%&'*+/=?`{|}~^-]+(?:\.[A-Z0-9_!#$%&'*+/=?`{|}~^-]+)*@[A-Z0-9-]+(?:\.[A-Z0-9-]+)+$/i)];

    constructor(private formBuilder: FormBuilder,
                private userHttpService: UserHttpService) {
        super();
    }

    get newEmailFormControl() {
        return this.backupEmailForm.get('newEmail');
    }

    get confirmNewEmailFormControl() {
        return this.backupEmailForm.get('confirmNewEmail');
    }

    ngOnInit(): void {
        this.errors = ErrorsUtil.create();
        if (this.authenticated) {
            this.submitButtonClass = 'btn btn-primary';
        }
        this.errors.fieldErrors = { newEmail: [], confirmNewEmail: [] };
        this.backupEmailForm = this.formBuilder.group({
            newEmail: [''], confirmNewEmail: ['']
        });
        // Do not replace! This needs to be lowercase transform as the user types rather than using only a validator.
        from([this.newEmailFormControl, this.confirmNewEmailFormControl]).pipe(
            mergeMap(formControl => formControl.valueChanges.pipe(
                tap((value: any) => formControl.setValue(value.toLowerCase(), { emitEvent: false }))
            )),
            takeUntil(this.ngUnsubscribe)
        ).subscribe();
        this.loading = false;
    }

    /**
     * Validator that ensures all fields in the formGroup match.
     * Email matching _MUST_ be case-sensitive for the local part as per RFC-2821 and RFC-5321
     */
    fieldsMatchValidator: ValidatorFn = (formGroup: AbstractControl): ValidationErrors | null => {
        const valueList = Object.values(formGroup.value);
        return (valueList.every(value => value === valueList[0])) ? null : { doNotMatch: true };
    };

    existingEmailValidator: ValidatorFn = (formGroup: AbstractControl): ValidationErrors | null => {
        if (this.currentBackupEmail && formGroup.get('newEmail').value === this.currentBackupEmail) {
            return { matchesCurrentBackupEmail: true };
        } else if (this.primaryEmail && formGroup.get('newEmail').value === this.primaryEmail) {
            return { matchesPrimaryEmail: true };
        }
        return null;
    };

    buildErrors() {
        if (this.backupEmailForm.errors.doNotMatch) {
            this.errors.globalErrors = ErrorsUtil.buildMessage('Provided Emails do not match');
        } else if (this.backupEmailForm.errors.matchesCurrentBackupEmail) {
            this.errors.globalErrors = ErrorsUtil.buildMessage('Recovery email cannot be the same as current recovery email');
        } else if (this.backupEmailForm.errors.matchesPrimaryEmail) {
            this.errors.globalErrors = ErrorsUtil.buildMessage('Recovery email cannot be the same as primary email');
        }
    }

    /**
     * Sets the backup email for the userId. We specifically retain the capitalization of the email as provided by the user.
     * This is because at least the local part of an email MUST remain case-sensitive for the use-case of sending/receiving emails.
     * For more information see [RFC-2821](https://www.ietf.org/rfc/rfc2821.txt), [RFC-5321](https://www.ietf.org/rfc/rfc5321.txt)
     * and [CVE-2019-19844](https://nvd.nist.gov/vuln/detail/CVE-2019-19844)
     * @param userId of user
     * @param email to set as backup email
     */
    setBackupEmailRequest(userId, email) {
        this.submitting = true;
        this.userHttpService.updateBackupEmailAddress(userId, { backupEmailAddress: email }).pipe(
            tap(() => this.backupEmailFormSubmitter.emit({ newEmail: this.newEmailFormControl.value })),
            catchError((err) => {
                this.errors = ErrorsUtil.mapResponse(err);
                return of(null);
            }),
            finalize(() => {
                this.submitting = false;
            }),
            takeUntil(this.ngUnsubscribe)
        ).subscribe();
    }

    submitEmail() {
        ErrorsUtil.clear(this.errors);
        this.validateForm();
        if (this.backupEmailForm.valid) {
            this.setBackupEmailRequest(this.userId, this.newEmailFormControl.value);
        } else {
            this.buildErrors();
        }
    }

    validateForm() {
        [this.newEmailFormControl, this.confirmNewEmailFormControl, this.backupEmailForm].forEach(
            formControl => {
                const validators = formControl === this.backupEmailForm
                    ? [this.fieldsMatchValidator, this.existingEmailValidator]
                    : this.emailFieldValidators;
                formControl.setValidators(validators);
                formControl.updateValueAndValidity();
                formControl.clearValidators();
            }
        );
    }

    cancel() {
        this.componentCloser.emit({ action: 'cancel' });
    }

    skip() {
        this.componentCloser.emit({ action: 'skip' });
    }
}
